import { useCallback, useEffect, useState } from "react";

import { formatWhen } from "../alarm-view.js";
import { ApiError } from "../api/client.js";
import { AdminTab } from "../components/AdminTab.js";
import { Icon } from "../components/Icon.js";
import { Toggle } from "../components/Toggle.js";
import { AlarmsTab } from "../components/AlarmsTab.js";
import { BackupTab } from "../components/BackupTab.js";
import { SystemTab } from "../components/SystemTab.js";
import { ClustersTab } from "../components/ClustersTab.js";
import { AnalyticsTab } from "../components/AnalyticsTab.js";
import type { Cluster } from "../components/ClusterCard.js";
import type { TelemetrySnapshot } from "../api/telemetry.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * Configurazione.
 *
 * Ogni scheda chiede al server quello che le serve e mostra quello che
 * ottiene: **niente permessi indovinati dal livello dell'utente**. La matrice
 * e' personalizzabile, quindi dedurre da "livello 4" cosa si puo' fare
 * darebbe una schermata che promette azioni poi respinte, o ne nasconde di
 * consentite. Se una chiamata torna 403, la scheda lo dice.
 */

type Tab = "clusters" | "alarms" | "roles" | "system" | "admin" | "backup" | "analytics";

/**
 * Le schede, nell'ordine e con le etichette dell'app desktop, e ognuna con il
 * permesso che la apre.
 *
 * Nasconderle a chi non le puo' usare non e' un vezzo: una scheda che si apre
 * e risponde 403 su ogni chiamata sembra un programma rotto. Il controllo vero
 * resta comunque sulle rotte.
 */
const TABS: { id: Tab; key: string; icon: string; perm?: string; level?: number }[] = [
  { id: "clusters", key: "settings.clusterSettings", icon: "settings-2", perm: "manageClusters" },
  { id: "alarms", key: "settings.alarmManagement", icon: "alert-triangle", perm: "manageAlarms" },
  { id: "roles", key: "settings.roles", icon: "shield", level: 5 },
  { id: "system", key: "settings.system", icon: "monitor", perm: "manageSystem" },
  { id: "admin", key: "settings.administration", icon: "users", perm: "manageUsers" },
  { id: "backup", key: "settings.backupRestore", icon: "database", perm: "manageUsers" },
  { id: "analytics", key: "analytics", icon: "bar-chart-3", perm: "viewEmissions" },
];

export function Settings({
  onManageUsers,
  onCalibrate,
  telemetry,
  onOpenCluster,
  onBack,
}: {
  onManageUsers?: (() => void) | undefined;
  onCalibrate?: ((c: Cluster) => void) | undefined;
  /** La telemetria dal vivo serve alla scheda ANALYTICS: perdite attive e
   *  stato degli apparati vengono da li'. */
  telemetry?: TelemetrySnapshot | undefined;
  onOpenCluster?: ((c: Cluster) => void) | undefined;
  onBack?: (() => void) | undefined;
}) {
  const { t } = useI18n();
  const { can, phase } = useSession();
  const level = phase.kind === "authenticated" ? phase.user.level : 0;
  const visible = TABS.filter(
    (x) => (x.perm === undefined || can(x.perm)) && (x.level === undefined || level >= x.level),
  );
  const [tab, setTab] = useState<Tab>("clusters");
  // Chi non puo' vedere la prima scheda deve comunque atterrare su una che
  // esiste, invece che su un pannello vuoto.
  const active = visible.some((x) => x.id === tab) ? tab : visible[0]?.id;

  return (
    <div className="settings">
      {/* Intestazione di `Settings.qml`: bottone indietro tondo, titolo e
          sottotitolo spaziato. Il logo Pergam che il .qml mette a destra qui
          NON si ripete: la' Settings occupa tutta la finestra, qui la barra
          dell'applicazione lo mostra gia' due centimetri piu' in alto. */}
      <header className="settings__head">
        <div className="settings__headtop">
          {onBack && (
            <button className="settings__back" onClick={onBack} title={t("nav.gridView")}>
              <Icon name="arrow-left" size={20} />
            </button>
          )}
          <div>
            <h2 className="settings__title">{t("settings.title")}</h2>
            <span className="settings__sub">{t("settings.systemConfig").toUpperCase()}</span>
          </div>
        </div>

        {/* Tab centrate, icona + etichetta, sottolineate quando attive: e' la
            riga di `Settings.qml`, non una barra di pulsanti. */}
        <nav className="settings__tabs">
          {visible.map((x) => (
            <button
              key={x.id}
              className={`stab${active === x.id ? " stab--on" : ""}`}
              onClick={() => setTab(x.id)}
            >
              <Icon name={x.icon} size={16} />
              <span>{t(x.key)}</span>
            </button>
          ))}
        </nav>
      </header>

      {/* Il contenuto sta in 1280 px centrati, come il contenitore del .qml:
          a tutta larghezza le tre cifre degli indicatori finivano agli
          antipodi dello schermo. */}
      <div className="settings__body">
        {active === "clusters" && <ClustersTab onCalibrate={onCalibrate} />}
        {active === "alarms" && <AlarmsTab />}
        {active === "roles" && <RolesTab />}
        {active === "system" && <SystemTab />}
        {active === "admin" && <AdminTab onManageUsers={() => onManageUsers?.()} />}
        {active === "backup" && <BackupTab />}
        {active === "analytics" && (
          <AnalyticsTab telemetry={telemetry ?? {}} onOpenCluster={onOpenCluster} />
        )}
      </div>
    </div>
  );
}

/** Errori uguali per tutte le schede: 403 e' una risposta, non un guasto. */
function useTabState() {
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const handle = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.isForbidden) setDenied(true);
    else setError(e instanceof ApiError ? e.message : String(e));
  }, []);
  const clear = useCallback(() => {
    setError("");
    setDenied(false);
  }, []);
  return { error, denied, handle, clear };
}

function Denied() {
  const { t } = useI18n();
  return (
    <div className="panel">
      <h3>{t("users.accessDenied")}</h3>
      <p className="muted">{t("logs.noPermission")}</p>
    </div>
  );
}

// ── impianti ───────────────────────────────────────────────────────────────

/** I campi che si modificano da qui. Non sono tutte le 98 colonne: sono
 *  quelle che si toccano davvero in messa in servizio, e ognuna corrisponde
 *  1:1 a un campo scrivibile del server — che rifiuta comunque il resto. */
/** Le etichette sono chiavi ESISTENTI in tutte e nove le lingue. Dove non
 *  c'era una chiave dedicata si riusa quella con lo stesso significato
 *  (`ftp.user` = "Username"): inventarne una nuova vorrebbe dire aggiungerla a
 *  mano a nove blocchi, e fino ad allora otto interfacce su nove mostrerebbero
 *  una parola inglese in mezzo. */
/**
 * La matrice dei permessi, portata da `SettingsRolesTab.qml`.
 *
 * **Permesso per riga, livello per colonna**, come nell'originale: le righe
 * sono quindici e i livelli cinque, e la tabella trasposta — quindici colonne
 * — non si legge. Le voci hanno il loro nome tradotto, non la chiave del
 * codice: `deleteSystemLog` non dice a nessuno cosa autorizza.
 *
 * Il livello 5 e' spuntato e bloccato ovunque: il superamministratore puo'
 * tutto per costruzione, e la matrice non lo governa. Lasciare quelle caselle
 * modificabili prometterebbe un controllo che non esiste.
 */
const PERM_KEYS = [
  "acknowledgeAlarms", "controlPtz", "recordTraces", "accessSettings",
  "manageClusters", "accessStreaming", "managePlantImages", "deleteTraces",
  "manageUsers", "changeLanguage", "viewEmissions", "exportReports",
  "manageAlarms", "manageSystem", "deleteSystemLog",
];

const ROLE_KEYS = [
  "roles.viewer", "roles.operator", "roles.supervisor", "roles.admin", "roles.superadmin",
];

function RolesTab() {
  const { t } = useI18n();
  const { api } = useSession();
  const { error, denied, handle, clear } = useTabState();
  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>({});
  /** Modifiche non ancora scritte. Finche' c'e' roba qui, SALVA e' acceso. */
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      setMatrix(await api.call<Record<string, Record<string, boolean>>>("role-permissions.list"));
      setDirty(false);
      clear();
    } catch (e) {
      handle(e);
    }
  }, [api, clear, handle]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // La spunta cambia **solo qui**, e sul server ci va con SALVA — come nel
  // `.qml`. Scrivere a ogni clic vuol dire che un tocco sbagliato toglie il
  // permesso a tutti gli operatori nell'istante in cui lo si sfiora, e su una
  // matrice di 75 caselle succede. La levetta qui non promette effetto
  // immediato: lo dice il bottone acceso accanto al titolo.
  const toggle = (level: number, key: string, value: boolean) => {
    if (level === 5) return;
    setMatrix((m) => ({ ...m, [String(level)]: { ...m[String(level)], [key]: value } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Un livello per chiamata, 1..4: il livello 5 il server lo rifiuta —
      // e' l'unico che puo' rimettere a posto la matrice.
      for (const level of [1, 2, 3, 4]) {
        await api.call("role-permissions.update", { level, ...matrix[String(level)] });
      }
      await reload();
    } catch (e) {
      // Niente `reload()` se qualcosa e' stato rifiutato: le modifiche
      // restano in pagina e SALVA resta acceso, altrimenti si crede di aver
      // salvato permessi che il server non ha scritto.
      handle(e);
    }
    setSaving(false);
  };

  if (denied) return <Denied />;

  return (
    <div className="settings__pane">
      {error && <p className="alert">{error}</p>}

      {/* Titolo, descrizione e i due bottoni sulla stessa riga, come nel
          `.qml`: RIPRISTINA sta accanto a SALVA perche' sono le due uscite
          della stessa modifica. */}
      <div className="roles__head">
        <div>
          <h3 className="roles__title">{t("settings.roles")}</h3>
          <p className="muted small">{t("settings.rolesDesc")}</p>
        </div>
        <span className="emissions__spacer" />
        <button onClick={() => void api.call("role-permissions.reset").then(reload).catch(handle)}>
          {t("settings.resetDefaults")}
        </button>
        <button
          className={dirty ? "btn--primary" : undefined}
          disabled={!dirty || saving}
          onClick={() => void save()}
        >
          {saving ? t("settings.saving") : t("settings.save")}
        </button>
      </div>

      <div className="atable">
        <table className="table permtable">
          <thead>
            <tr>
              <th>{t("settings.permission").toUpperCase()}</th>
              {ROLE_KEYS.map((rk, i) => (
                <th key={rk} className="ta-center">
                  <div>LVL {i + 1}</div>
                  <div className="muted small">{t(rk)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERM_KEYS.map((k) => (
              <tr key={k}>
                <td>{t(`perm.${k}`)}</td>
                {[1, 2, 3, 4, 5].map((l) => (
                  <td key={l} className="ta-center">
                    {/* Il livello 5 e' acceso e non si tocca: e' la chiave
                        maestra, e poterla spegnere vorrebbe dire potersi
                        chiudere fuori dal programma. Nel .qml e' la stessa
                        levetta, solo smorzata. */}
                    <Toggle
                      on={l === 5 ? true : matrix[String(l)]?.[k] === true}
                      disabled={l === 5}
                      onChange={(v) => toggle(l, k, v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* La nota sul livello 5 sta **dopo** la matrice, come nel `.qml`: e' la
          spiegazione della colonna smorzata, e prima di vederla non vuol dire
          niente. */}
      <p className="muted small">{t("settings.rolesLevel5Note")}</p>
    </div>
  );
}
